import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { CheckCircle, Download, Mail, Loader2 } from "lucide-react";

export const OrderConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // If coming from Stripe, poll for payment status
        if (sessionId) {
          let attempts = 0;
          const maxAttempts = 5;
          const pollInterval = 2000;

          while (attempts < maxAttempts) {
            const statusRes = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
            
            if (statusRes.data.payment_status === "paid") {
              break;
            }
            
            attempts++;
            if (attempts < maxAttempts) {
              await new Promise((resolve) => setTimeout(resolve, pollInterval));
            }
          }
        }

        // Fetch order details
        if (orderId) {
          const orderRes = await axios.get(`${API}/orders/${orderId}`);
          setOrder(orderRes.data);
          
          // Clear cart after successful payment
          await clearCart();
        }
      } catch (e) {
        console.error("Error verifying payment:", e);
        setError("Unable to verify payment. Please contact support if you completed payment.");
      } finally {
        setVerifying(false);
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId, orderId]);

  if (loading || verifying) {
    return (
      <div className="py-24 px-6 text-center max-w-md mx-auto" data-testid="order-loading">
        <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-6" />
        <h1 className="font-serif text-2xl mb-4">Verifying Your Payment</h1>
        <p className="text-muted-foreground">Please wait while we confirm your order...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="py-24 px-6 text-center max-w-md mx-auto" data-testid="order-error">
        <h1 className="font-serif text-2xl mb-4">Something Went Wrong</h1>
        <p className="text-muted-foreground mb-8">
          {error || "Unable to find your order. Please contact support."}
        </p>
        <Link to="/shop">
          <Button className="rounded-none px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold">
            Return to Shop
          </Button>
        </Link>
      </div>
    );
  }

  const isPaid = order.payment_status === "paid";

  return (
    <div className="py-12 md:py-24 px-6 md:px-12 max-w-2xl mx-auto" data-testid="order-confirmation-page">
      <div className="text-center mb-12">
        {isPaid ? (
          <>
            <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-6" />
            <h1 className="font-serif text-4xl md:text-5xl mb-4">Thank You!</h1>
            <p className="text-muted-foreground text-lg">
              Your order has been confirmed and your download links are ready.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-amber-500 mb-6" />
            <h1 className="font-serif text-4xl md:text-5xl mb-4">Payment Pending</h1>
            <p className="text-muted-foreground text-lg">
              Your payment is being processed. You'll receive your download links once confirmed.
            </p>
          </>
        )}
      </div>

      {/* Order Details */}
      <div className="bg-muted/30 p-6 md:p-8 mb-8">
        <h2 className="font-serif text-xl mb-6">Order Details</h2>
        
        <div className="space-y-4 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Order ID</span>
            <span className="font-mono text-xs">{order.id}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Email</span>
            <span>{order.email}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Payment Method</span>
            <span className="capitalize">{order.payment_method}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Status</span>
            <span className={isPaid ? "text-green-600 font-medium" : "text-amber-600 font-medium"}>
              {isPaid ? "Paid" : "Pending"}
            </span>
          </div>
        </div>

        <div className="border-t border-border pt-4 space-y-3">
          <h3 className="text-sm font-medium mb-3">Items</h3>
          {order.items.map((item) => (
            <div key={item.artwork_id} className="flex justify-between text-sm">
              <span>{item.title}</span>
              <span>${item.price.toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-4 mt-4">
          <div className="flex justify-between font-medium text-lg">
            <span>Total</span>
            <span>${order.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Download Section */}
      {isPaid && (
        <div className="bg-green-50 border border-green-200 p-6 md:p-8 mb-8">
          <div className="flex items-start gap-4">
            <Download className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-medium text-green-800 mb-2">Your Downloads Are Ready</h3>
              <p className="text-sm text-green-700 mb-4">
                Click the button below to access your digital artwork downloads.
              </p>
              <Link to={`/download/${order.download_token}`} target="_blank">
                <Button 
                  className="rounded-none bg-green-600 hover:bg-green-700 text-white uppercase tracking-widest text-xs font-bold"
                  data-testid="download-btn"
                >
                  Access Downloads
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Email Notice */}
      <div className="flex items-start gap-4 p-6 border border-border">
        <Mail className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-1" />
        <div>
          <h3 className="font-medium mb-2">Check Your Email</h3>
          <p className="text-sm text-muted-foreground">
            We've sent a confirmation email to <strong>{order.email}</strong> with your download links 
            and order details. Please check your spam folder if you don't see it.
          </p>
        </div>
      </div>

      {/* Continue Shopping */}
      <div className="text-center mt-12">
        <Link to="/shop">
          <Button 
            variant="outline"
            className="rounded-none px-8 py-3 uppercase tracking-widest text-xs font-bold"
            data-testid="continue-shopping-btn"
          >
            Continue Shopping
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
